/**
 * The team's state module (spec 2026-09-06 §3.3, §3.4, §3.5, §3.9, §4.1) — one
 * derivation for every word a team surface says about a lane.
 *
 * The bench, the lane screen, the return line, the delegate step, the composer's
 * @ list and Bash's own line all name a lane's state. Before this module each
 * kept its own words, and they drifted: the same finished helper read "finished"
 * in one place and "done" in another, and a lane the person had killed still
 * read as a clean finish somewhere. Every surface reads from here instead.
 *
 * Pure and synchronous. Nothing renders, no store is touched.
 */

import type { SubagentReportStopReason } from "@bash/shared";
import type { FaceMood } from "@/components/bash-home/BashFace";
import { FAMILY_DESK, type Family } from "@/components/task-story/derive";
import { messagePeer } from "@/lib/teammates";
import type { Message } from "@/stores/types";
import type { ToolUseBlock } from "@/types";

export type LaneWord =
  | { kind: "live"; word: string }
  | { kind: "needs_you"; word: "waiting for you" }
  | { kind: "ended"; word: string }
  | { kind: "starting"; word: "starting" };

/**
 * The word an ended lane reads, keyed by the reason its report carried.
 *
 * Keyed by the CLOSED report set, so a seventh reason added to the wire is a
 * compile error here rather than a lane that silently reads "done". The lookup
 * in `endedWord` still tolerates an unknown string, because the wire is
 * deliberately lenient about reasons this build has not heard of.
 */
export const ENDED_WORD: Record<SubagentReportStopReason, string> = {
  completed: "done",
  stopped_by_user: "stopped",
  stopped_by_parent: "stopped by Bash",
  turn_cap: "hit its turn limit",
  failed: "failed",
  credits_exhausted: "out of credits",
};

/**
 * The word an ended lane reads. A reason that NAMES how the run ended wins.
 * `completed` does not: it is the one report that says nothing the lane's own
 * terminal cannot override, and a stale `completed` from an earlier turn beside
 * a kill whose report never landed is exactly what `endedInterrupted` is for.
 * Absent and unknown reasons take the same path, and an ending with nothing to
 * say about it is the clean one.
 */
export function endedWord(
  stopReason: string | undefined,
  endedInterrupted = false
): string {
  // The cast is a lie for an unknown wire string, so the annotation tells the
  // truth: the lookup can miss, and the fallback below is what catches it.
  const named: string | undefined =
    stopReason && stopReason !== "completed"
      ? ENDED_WORD[stopReason as SubagentReportStopReason]
      : undefined;
  return named ?? (endedInterrupted ? "stopped" : ENDED_WORD.completed);
}

/**
 * §3.4 — a helper is its brief, a teammate its @name. A helper has no handle
 * the person chose, so the description it was spawned with is the only name it
 * has; a teammate's own handle is the name, and a description would rename a
 * lane the person already knows.
 */
export function laneTitle(meta: {
  kind?: "teammate" | "helper";
  description?: string;
  name: string;
}): string {
  return meta.kind === "helper" && meta.description
    ? meta.description
    : `@${meta.name}`;
}

/** A lane pausing to interrupt is still alive; only an idle one has ended. */
const LIVE_PHASES = new Set(["processing", "waiting", "interrupting"]);

/**
 * §3.3, the rules in order. `liveFamily` is the lane's newest live step family,
 * if any — the same word Bash's own line reads for the same tool.
 */
export function laneWord(input: {
  phase: string | undefined;
  pendingInteraction: unknown | null | undefined;
  threadLength: number;
  liveFamily: Family | null;
  stopReason: string | undefined;
  endedInterrupted: boolean;
}): LaneWord {
  // Above the phase check: a lane paused on a question can still read
  // `processing`, and it is the one state that needs a human.
  if (input.pendingInteraction)
    return { kind: "needs_you", word: "waiting for you" };
  if (input.phase !== undefined && LIVE_PHASES.has(input.phase)) {
    return {
      kind: "live",
      word: input.liveFamily ? FAMILY_DESK[input.liveFamily].verb : "working",
    };
  }
  // A lane is minted IDLE and stays idle until its first turn starts, so an
  // empty thread is a lane starting up, never one that ended saying nothing.
  if (input.threadLength === 0) return { kind: "starting", word: "starting" };
  return {
    kind: "ended",
    word: endedWord(input.stopReason, input.endedInterrupted),
  };
}

export const isLiveWord = (w: LaneWord) =>
  w.kind === "live" || w.kind === "needs_you" || w.kind === "starting";

/**
 * A live verb's face, READ BACK from the desk's own table rather than copied
 * into a second one. Every verb the ladder can say is a `FAMILY_DESK` entry, so
 * the map is the source, not a transcription of it.
 */
const MOOD_BY_VERB = new Map<string, FaceMood>(
  Object.values(FAMILY_DESK).map((d) => [d.verb, d.mood])
);

/**
 * §3.4, D4 — the face an ENDING wears, keyed off `ENDED_WORD` rather than typed
 * again. There were two of these too, and they disagreed: a lane that failed
 * was puzzled on its return line and content on its chip and its own screen, so
 * the same helper wore two faces on one screen. A clean finish smiles, an
 * ending that went wrong looks puzzled, and every other way of stopping rests.
 */
const MOOD_BY_ENDING = new Map<string, FaceMood>([
  [ENDED_WORD.completed, "happy"],
  [ENDED_WORD.turn_cap, "puzzled"],
  [ENDED_WORD.failed, "puzzled"],
]);

export function endingMood(word: string): FaceMood {
  return MOOD_BY_ENDING.get(word) ?? "content";
}

/**
 * §3.6, D4 — the face a lane's word wears, on the chip and on its own screen.
 *
 * There were two of these and they disagreed: a starting lane was waking on the
 * bench and thinking on the screen, and one of them decided "reading" by a
 * two-word check while the other asked `FAMILY_DESK`. One word, one face, one
 * place — the same law the word itself obeys.
 */
export function moodOf(word: LaneWord): FaceMood {
  if (word.kind === "needs_you") return "waiting";
  if (word.kind === "ended") return endingMood(word.word);
  if (word.kind === "starting") return "waking";
  // The ended words and the generic "working" are not in the table; "working"
  // is what they mean anyway.
  return MOOD_BY_VERB.get(word.word) ?? "working";
}

/**
 * §4.1, R18 — the STATE half of the line. Bold on every surface that says it,
 * with the names in the line's quiet ink after it, so a lead with four long
 * titles out is not a wall of heavy text where the eye is scanning for a state.
 * The one place the words live, so the trigger and the copy cannot drift.
 */
export const WAITING_ON = "waiting on";

/**
 * §4.1 — the NAMES half: "A, B and C". Three or fewer lanes are all named; more
 * are cut to three and counted, so a lead with eight helpers out still reads as
 * a sentence rather than a list.
 */
export function waitingOnList(titles: string[]): string {
  if (titles.length === 0) return "";
  const head = titles.length <= 3 ? titles.slice(0, -1) : titles.slice(0, 3);
  const tail =
    titles.length <= 3
      ? titles[titles.length - 1]
      : `${titles.length - 3} more`;
  return head.length > 0 ? `${head.join(", ")} and ${tail}` : tail;
}

/**
 * §4.1 — the whole sentence, the founder's exact-state rule. Built FROM the
 * list half rather than beside it: the desk's verb is one string (`DeskState`),
 * and the line that splits it back apart has to land on the same words.
 */
export function waitingOnSentence(titles: string[]): string {
  return titles.length === 0 ? "" : `${WAITING_ON} ${waitingOnList(titles)}`;
}

/**
 * Spec 2026-09-12 (R2) — the line is the MECHANISM by which a person learns a
 * teammate is stuck on them, so a stuck lane must not hide inside the working
 * ones. `isLiveWord` counts `needs_you` as live, which is right for "is this lane
 * over", and wrong here: it folded "Windsurf pricing is waiting on you" into
 * "Bash is waiting on Cursor pricing, Windsurf pricing and Devin pricing", where
 * it reads as one of three healthy helpers.
 *
 * Derived, never spoken by the model (R2): if Bash had to SAY it, a Bash that
 * fails to say it is a silent stall, which is the bug this round exists to close.
 *
 * `needsYou` empty gives back `waitingOnSentence(working)` byte for byte, so every
 * line without a stuck lane reads exactly as it does today.
 */
export function teamStateSentence(
  needsYou: string[],
  working: string[]
): string {
  if (needsYou.length === 0) return waitingOnSentence(working);
  const stuck =
    needsYou.length === 1
      ? `${needsYou[0]} is waiting on you`
      : `${waitingOnList(needsYou)} are waiting on you`;
  if (working.length === 0) return stuck;
  const rest =
    working.length === 1
      ? `${working[0]} is still working`
      : `${waitingOnList(working)} are still working`;
  return `${stuck}. ${rest}`;
}

/** §4.1 — the chip's short form, where a full list has nowhere to sit. */
export function shortWaitingOn(titles: string[]): string {
  if (titles.length === 0) return "";
  return titles.length === 1
    ? `${WAITING_ON} ${titles[0]}`
    : `${WAITING_ON} ${titles.length} helpers`;
}

/**
 * §3.9 — the sandbox writes a mechanical preamble for the LEAD's model context
 * (`apps/sandbox/src/stream/teammates.ts`). The row's own attribution header
 * already says who reported, so showing the wrapper too reads as broken
 * plumbing. Stripped for DISPLAY only; the stored text is untouched.
 *
 * The handle group is optional because the preamble is minted without one while
 * the teamwork flag is off (`Helper "brief" finished and reports:`), and an
 * unstripped preamble is exactly the defect this exists to prevent.
 */
const NOTE_PARAGRAPH = /^NOTE: [^\n]*\n\n/;
const HELPER_PREAMBLE =
  /^Helper (?:@\S+ \("[^"]*"\)|"[^"]*") (finished and reports|replied|stopped and reports):\s*/;
const TEAMMATE_PREAMBLE =
  /^Teammate "[^"]+" (finished its turn and reports|reports):\s*/;

const REPORT_VERB: Record<string, "finished" | "replied" | "stopped" | null> = {
  "finished and reports": "finished",
  replied: "replied",
  "stopped and reports": "stopped",
  "finished its turn and reports": "finished",
  reports: null,
};

export function splitReport(text: string): {
  verb: "finished" | "replied" | "stopped" | null;
  body: string;
} {
  // The NOTE paragraph is only ever minted directly ABOVE a "stopped and
  // reports" header, so it is dropped only when one follows: a body that opens
  // with a note of its own is the helper's text, and §3.9 allows no other
  // rewriting.
  const afterNote = text.replace(NOTE_PARAGRAPH, "");
  const match =
    HELPER_PREAMBLE.exec(afterNote) ?? TEAMMATE_PREAMBLE.exec(afterNote);
  if (!match) return { verb: null, body: text.trim() };
  // The held-items tail stays in the body: an item a helper still holds is
  // information the lead needs, not plumbing.
  return {
    verb: REPORT_VERB[match[1]] ?? null,
    body: afterNote.slice(match[0].length).trim(),
  };
}

/** A relayed row's own words: every text block it carries, in order. */
export function reportText(message: Pick<Message, "content">): string {
  return message.content
    .filter((b) => b.type === "text")
    .map((b) => (b as { text?: string }).text ?? "")
    .join("\n\n");
}

/**
 * §3.8, R20 — does this `to_lead` row carry a REPORT?
 *
 * The channel a lane reports on carries more than reports. A mid-run
 * `ReportToLead` update is bare text ("progress, results, questions, or
 * blockers", its own description), and so is the notice the sandbox mints when
 * a teammate dies mid-turn: `Teammate "X" stopped without completing its turn
 * … Check on it with SendMessage`. Folding those into the return line said a
 * helper that CRASHED had finished, put a clock on it, and hid the very notice
 * telling the person to go look.
 *
 * A report is a row that says how the run ended, and it says so in one of two
 * structured ways: the sandbox's preamble (§3.9), or the stop reason the wire
 * stamped on the attribution. A row with neither is a message, and a message
 * stays visible.
 */
export function isLaneReport(
  message: Pick<Message, "content">,
  stopReason: string | undefined
): boolean {
  return stopReason != null || splitReport(reportText(message)).verb !== null;
}

/** §3.5 — one moment in a lane's life, on the lead's clock. */
export interface LaneEvent {
  at: number;
  kind: "spawn" | "return" | "resume";
}

/** The store types a row's timestamp a Date; `new Date` also survives a row
 *  rehydrated from JSON, where it arrives as the string it was stored as — the
 *  shape a persisted tool block's `startTime` comes back in. */
const msOf = (t: Date | string): number => new Date(t).getTime();

/**
 * §3.5 — the LEAD-side half of a lane's life: when it was sent off, and every
 * time it reported back. The bench reads a lane's status now; history needs the
 * past, so each finished line of Bash's can say who was out when it ended.
 *
 * The spawn is the DELEGATING BLOCK's own start — the moment the lane was sent
 * off. Not the message's: the web mints one assistant message per lead turn,
 * stamped at AGENT_START (a continuation even re-adopts the previous turn's),
 * so a lane sent off after four minutes of Bash's own reading rode a timestamp
 * four minutes early and every chip on the bench was offset by that work. The
 * block's start is on the wire and survives a reload; the message's timestamp
 * stands in only for a block that carries none.
 *
 * A return is a `to_lead` peer row that carries a REPORT (R20, `isLaneReport`).
 * The channel carries more: a teammate is told to send interim updates on it,
 * and a bare update closed the lane in history — so a turn of Bash's that ended
 * between an update and the report read done for a lane still out.
 *
 * The lead's thread has nothing else to say about the lane. A resume lives on
 * the LANE's own thread; `laneTimelineFromThreads` is the whole reading.
 */
export function laneTimeline(
  leadMessages: readonly Pick<Message, "role" | "content" | "timestamp">[],
  laneId: string,
  parentToolUseId: string
): LaneEvent[] {
  const events: LaneEvent[] = [];
  for (const m of leadMessages) {
    if (m.role !== "assistant") continue;
    const block = m.content.find(
      (b) =>
        b.type === "tool_use" &&
        (b as ToolUseBlock).toolUseId === parentToolUseId
    ) as ToolUseBlock | undefined;
    if (!block) continue;
    const startTime = block.startTime as Date | string | undefined;
    events.push({ at: msOf(startTime ?? m.timestamp), kind: "spawn" });
    break;
  }
  for (const m of leadMessages) {
    if (m.role !== "user") continue;
    const peer = messagePeer(m);
    if (!peer || peer.direction !== "to_lead" || peer.peerTaskId !== laneId)
      continue;
    if (!isLaneReport(m, peer.stopReason)) continue;
    events.push({ at: msOf(m.timestamp), kind: "return" });
  }
  // Array#sort is stable, so the spawn — pushed first — still opens a timeline
  // whose first return shares its millisecond.
  return events.sort((a, b) => a.at - b.at);
}

/**
 * §3.5, R25 — a lane's whole life, read from BOTH threads.
 *
 * The lead's thread records the sending and the coming back. It does NOT record
 * the sending out AGAIN: the only row a resume ever mints is addressed to the
 * LANE, and the server persists it on the lane's own thread — the sandbox's
 * relay (`SendMessage`) and a person's plain message to the lane alike. Reading
 * a resume off the lead's thread therefore found nothing on the real wire, and
 * every clock stayed the lifetime the R17 ruling was minted to retire: a helper
 * picked back up hours later read "done · 361m" for a run of seconds, and no
 * finished line of Bash's ever said a lane was out again.
 *
 * So a resume is a USER row on the LANE's thread that RE-OPENS a closed lane —
 * one whose latest beat is a return. A row that does not (the brief the lane
 * was minted with, a re-delivered brief, a nudge to a lane already out) is no
 * event at all: it starts no run, and a nudge must not restart the clock of a
 * run that is still going.
 */
export function laneTimelineFromThreads(input: {
  leadMessages: readonly Pick<Message, "role" | "content" | "timestamp">[];
  laneMessages: readonly Pick<Message, "role" | "timestamp">[] | undefined;
  laneId: string;
  parentToolUseId: string;
}): LaneEvent[] {
  const lead = laneTimeline(
    input.leadMessages,
    input.laneId,
    input.parentToolUseId
  );
  const asked = (input.laneMessages ?? [])
    .filter((m) => m.role === "user")
    .map((m) => msOf(m.timestamp))
    .sort((a, b) => a - b);
  if (asked.length === 0) return lead;

  const events: LaneEvent[] = [];
  let last: LaneEvent["kind"] | null = null;
  let i = 0;
  let j = 0;
  while (i < lead.length || j < asked.length) {
    // The lead's own beat wins a tie: the brief a lane is minted with shares
    // the spawn's millisecond, and reading it as a resume would open a second
    // run before the first had begun.
    if (i < lead.length && (j >= asked.length || lead[i].at <= asked[j])) {
      last = lead[i].kind;
      events.push(lead[i]);
      i++;
      continue;
    }
    const at = asked[j];
    j++;
    if (last !== "return") continue;
    last = "resume";
    events.push({ at, kind: "resume" });
  }
  return events;
}

/**
 * §3.5 — was this lane out at `t`? Out from its spawn (or from a resume) until
 * it reports back. Takes a timeline SORTED by `at` (what `laneTimeline` hands
 * back): the scan below stops at the first event past `t`.
 */
export function laneOutAt(timeline: readonly LaneEvent[], t: number): boolean {
  let last: LaneEvent | undefined;
  for (const e of timeline) {
    if (e.at > t) break;
    last = e;
  }
  // Nothing has happened yet at `t`. A lane whose spawn we can SEE simply was
  // not born; a lane whose spawn block is missing counts as out from the
  // beginning of time, so a line never claims a task was done alone.
  if (!last) return !timeline.some((e) => e.kind === "spawn");
  return last.kind !== "return";
}

/**
 * §3.5, R17 — a lane's life, and the clock its surfaces read.
 *
 * `spawnedAt` is when it was first sent off and `endedAt` its last return
 * (null while it is out). The CLOCK is neither pair: it is the lane's LAST RUN.
 *
 * A run opens when the lane is sent off (the spawn) or sent out again (a
 * resume), and closes at the return after it. Measuring from the spawn instead
 * charged the new run with every minute since the first one: a helper the
 * person picked back up hours later read "done · 361m" on staging, for a run
 * that took seconds. `runStartedAt` is the last event that OPENS a run — while
 * the lane is out, the run still going (what the bench's live clock counts
 * from); once it is back, the run its return closed.
 */
export function laneSpan(timeline: readonly LaneEvent[]): {
  spawnedAt: number | null;
  endedAt: number | null;
  runStartedAt: number | null;
} {
  const spawn = timeline.find((e) => e.kind === "spawn");
  const last = timeline[timeline.length - 1];
  let runStartedAt: number | null = null;
  for (let i = timeline.length - 1; i >= 0; i--) {
    if (timeline[i].kind === "return") continue;
    runStartedAt = timeline[i].at;
    break;
  }
  return {
    spawnedAt: spawn ? spawn.at : null,
    endedAt: last && last.kind === "return" ? last.at : null,
    runStartedAt,
  };
}

/**
 * §3.5, R17 — the one run a single RETURN closed, for a line minted per report.
 *
 * `laneSpan` answers for the lane, which is what a bench chip is: one row per
 * lane, always its latest. A return line is one row per REPORT, and a lane that
 * reports twice leaves two of them standing in the story. Reading the lane's
 * span there gave the older line the newer run's duration, and took its clock
 * away entirely while the lane was out again on a second errand.
 *
 * So the line asks for the run its OWN row closed: the last return at or before
 * the row's moment, and the spawn or resume that opened it. A row with no
 * return behind it (a relay the timeline never saw) gets neither end, and shows
 * no clock rather than a wrong one.
 */
export function laneRunAt(
  timeline: readonly LaneEvent[],
  at: number
): { runStartedAt: number | null; endedAt: number | null } {
  let closed = -1;
  for (let i = 0; i < timeline.length; i++) {
    if (timeline[i].at > at) break;
    if (timeline[i].kind === "return") closed = i;
  }
  if (closed === -1) return { runStartedAt: null, endedAt: null };
  // Backwards to the event that OPENED that run, skipping any earlier return of
  // the same run (a lane can send an interim update and its report in one go).
  let runStartedAt: number | null = null;
  for (let i = closed - 1; i >= 0; i--) {
    if (timeline[i].kind === "return") continue;
    runStartedAt = timeline[i].at;
    break;
  }
  return { runStartedAt, endedAt: timeline[closed].at };
}

/**
 * §3.5, §4.2 — the lanes that were out at `t`, in roster order.
 *
 * What each FINISHED line of Bash's reads: not who is out now (the latest line
 * asks the live status for that), but who was out when that turn ended, so a
 * line that was true at 09:04 still says what was true at 09:04.
 *
 * The timeline rides the lane (R25): it is built ONCE where both threads are in
 * reach (`useLaneStates`), never per line and never per reader — walking the
 * lead's whole message array per lane per turn is what made a long story column
 * stutter on every streaming delta.
 *
 * A lane nothing is known about is SKIPPED. `laneOutAt` reads an empty timeline
 * as out from the beginning of time — the right answer for a lane whose spawn
 * block alone is missing, and the wrong one for a whole thread that has not
 * loaded yet, which would put the entire roster on every older line of a cold
 * reload.
 */
export function lanesOutFrom<L extends { timeline: readonly LaneEvent[] }>(
  lanes: readonly L[],
  t: number
): L[] {
  const out: L[] = [];
  for (const lane of lanes)
    if (lane.timeline.length > 0 && laneOutAt(lane.timeline, t)) out.push(lane);
  return out;
}
